import * as THREE from 'three';
import type { MaterialLibrary } from '../MaterialLibrary';

export type EnemyType = 'infantry' | 'tank' | 'landingcraft' | 'plane' | 'paratrooper';

export type EnemyModel = {
  root: THREE.Group;
  body: THREE.Object3D;
  radius: number;
  height: number;
};

export class EnemyFactory {
  constructor(private readonly materials: MaterialLibrary) {}

  create(type: EnemyType): EnemyModel {
    switch (type) {
      case 'infantry':
        return this.createInfantry();
      case 'tank':
        return this.createTank();
      case 'landingcraft':
        return this.createLandingCraft();
      case 'plane':
        return this.createPlane();
      case 'paratrooper':
        return this.createParatrooper();
      default:
        throw new Error(`Unknown enemy type: ${type}`);
    }
  }

  private createInfantry(): EnemyModel {
    const root = new THREE.Group();
    root.name = 'enemy-infantry';

    const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.18, 0.35, 4, 8), this.materials.alliedGreen);
    body.position.y = 0.42;
    body.castShadow = true;
    root.add(body);

    const head = new THREE.Mesh(new THREE.SphereGeometry(0.14, 8, 8), this.materials.alliedTan);
    head.position.y = 0.78;
    head.castShadow = true;
    root.add(head);

    const helmet = new THREE.Mesh(new THREE.SphereGeometry(0.16, 8, 8, 0, Math.PI * 2, 0, Math.PI / 2), this.materials.alliedGreen);
    helmet.position.y = 0.8;
    helmet.castShadow = true;
    root.add(helmet);

    const rifle = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.06, 0.48), this.materials.darkSteel);
    rifle.position.set(0.12, 0.52, 0.18);
    rifle.rotation.y = 0.2;
    rifle.castShadow = true;
    root.add(rifle);

    return { root, body, radius: 0.26, height: 0.9 };
  }

  private createTank(): EnemyModel {
    const root = new THREE.Group();
    root.name = 'enemy-tank';

    const hull = new THREE.Mesh(new THREE.BoxGeometry(1.1, 0.55, 1.6), this.materials.alliedGreen);
    hull.position.y = 0.5;
    hull.castShadow = true;
    root.add(hull);

    const tracksL = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.42, 1.7), this.materials.darkOlive);
    tracksL.position.set(-0.55, 0.32, 0);
    tracksL.castShadow = true;
    root.add(tracksL);

    const tracksR = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.42, 1.7), this.materials.darkOlive);
    tracksR.position.set(0.55, 0.32, 0);
    tracksR.castShadow = true;
    root.add(tracksR);

    const turret = new THREE.Mesh(new THREE.CylinderGeometry(0.35, 0.4, 0.35, 12), this.materials.alliedGreen);
    turret.position.y = 0.95;
    turret.castShadow = true;
    root.add(turret);

    const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.07, 1.1, 8), this.materials.darkSteel);
    barrel.rotation.x = Math.PI / 2;
    barrel.position.set(0, 0.95, 0.6);
    barrel.castShadow = true;
    root.add(barrel);

    const antenna = new THREE.Mesh(new THREE.CylinderGeometry(0.01, 0.01, 0.7, 4), this.materials.darkSteel);
    antenna.position.set(-0.25, 1.15, -0.45);
    root.add(antenna);

    return { root, body: hull, radius: 0.78, height: 1.2 };
  }

  private createLandingCraft(): EnemyModel {
    const root = new THREE.Group();
    root.name = 'enemy-landingcraft';

    const hullShape = new THREE.Shape();
    hullShape.moveTo(-0.7, 0);
    hullShape.lineTo(0.7, 0);
    hullShape.lineTo(0.5, 1.35);
    hullShape.lineTo(-0.5, 1.35);
    hullShape.lineTo(-0.7, 0);

    const extrudeSettings = { depth: 0.28, bevelEnabled: false };
    const hullGeo = new THREE.ExtrudeGeometry(hullShape, extrudeSettings);
    hullGeo.center();
    const hull = new THREE.Mesh(hullGeo, this.materials.alliedTan);
    hull.position.y = 0.32;
    hull.rotation.x = -Math.PI / 2;
    hull.rotation.z = Math.PI;
    hull.castShadow = true;
    root.add(hull);

    const bowRamp = new THREE.Mesh(new THREE.BoxGeometry(0.8, 0.06, 0.55), this.materials.rust);
    bowRamp.position.set(0, 0.32, 0.75);
    bowRamp.rotation.x = -0.45;
    bowRamp.castShadow = true;
    root.add(bowRamp);

    const cabin = new THREE.Mesh(new THREE.BoxGeometry(0.45, 0.4, 0.35), this.materials.alliedGreen);
    cabin.position.set(0, 0.62, -0.35);
    cabin.castShadow = true;
    root.add(cabin);

    for (let i = 0; i < 3; i++) {
      const soldier = new THREE.Mesh(new THREE.CapsuleGeometry(0.1, 0.22, 4, 6), this.materials.alliedGreen);
      soldier.position.set(-0.28 + i * 0.28, 0.5, 0.25);
      soldier.castShadow = true;
      root.add(soldier);
    }

    return { root, body: hull, radius: 0.72, height: 0.85 };
  }

  private createPlane(): EnemyModel {
    const root = new THREE.Group();
    root.name = 'enemy-plane';

    const fuselage = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.22, 1.4, 10), this.materials.alliedTan);
    fuselage.rotation.x = Math.PI / 2;
    fuselage.castShadow = true;
    root.add(fuselage);

    const wing = new THREE.Mesh(new THREE.BoxGeometry(2.2, 0.08, 0.45), this.materials.alliedGreen);
    wing.position.set(0, 0, 0.1);
    wing.castShadow = true;
    root.add(wing);

    const tail = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.08, 0.35), this.materials.alliedGreen);
    tail.position.set(0, 0, -0.65);
    tail.castShadow = true;
    root.add(tail);

    const prop = new THREE.Mesh(new THREE.BoxGeometry(1.0, 0.04, 0.04), this.materials.darkSteel);
    prop.position.set(0, 0, 0.72);
    root.add(prop);

    return { root, body: fuselage, radius: 1.1, height: 0.6 };
  }

  private createParatrooper(): EnemyModel {
    const root = new THREE.Group();
    root.name = 'enemy-paratrooper';

    const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.18, 0.35, 4, 8), this.materials.alliedGreen);
    body.position.y = 0.42;
    body.castShadow = true;
    root.add(body);

    const head = new THREE.Mesh(new THREE.SphereGeometry(0.14, 8, 8), this.materials.alliedTan);
    head.position.y = 0.78;
    head.castShadow = true;
    root.add(head);

    const helmet = new THREE.Mesh(new THREE.SphereGeometry(0.16, 8, 8, 0, Math.PI * 2, 0, Math.PI / 2), this.materials.alliedGreen);
    helmet.position.y = 0.8;
    helmet.castShadow = true;
    root.add(helmet);

    const canopy = new THREE.Mesh(new THREE.ConeGeometry(0.7, 0.25, 8, 1, true), this.materials.alliedTan);
    canopy.position.y = 1.45;
    canopy.castShadow = true;
    root.add(canopy);

    const line1 = new THREE.Mesh(new THREE.CylinderGeometry(0.01, 0.01, 0.55, 4), this.materials.darkSteel);
    line1.position.set(-0.25, 1.05, 0);
    line1.rotation.z = 0.2;
    root.add(line1);

    const line2 = new THREE.Mesh(new THREE.CylinderGeometry(0.01, 0.01, 0.55, 4), this.materials.darkSteel);
    line2.position.set(0.25, 1.05, 0);
    line2.rotation.z = -0.2;
    root.add(line2);

    return { root, body, radius: 0.28, height: 1.5 };
  }
}
